import { FC, useState, useEffect } from "react";
import type { Course, ApplicationFormDataV2, ApplicationResponse } from "../types";
import { fetchCourses, submitApplicationV2 } from "../services/api";
import { useBranding } from "../context/BrandingContext";
import Header from "../components/Header";
import ApplicationFormV2 from "../components/ApplicationFormV2";
import PaymentQR from "../components/PaymentQR";

/**
 * Landing Page V2 - Main application page
 * Light theme with brand colors
 */
const LandingPage: FC = () => {
  const brand = useBranding();

  // Course data
  const [courses, setCourses] = useState<Course[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [courseError, setCourseError] = useState<string | null>(null);

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Payment state
  const [paymentData, setPaymentData] = useState<ApplicationResponse | null>(null);

  // Fetch courses on mount
  useEffect(() => {
    const loadCourses = async () => {
      try {
        const data = await fetchCourses();
        setCourses(data);
      } catch (err) {
        setCourseError(err instanceof Error ? err.message : "Failed to load courses");
      } finally {
        setLoadingCourses(false);
      }
    };
    loadCourses();
  }, []);

  // Handle form submission
  const handleSubmit = async (data: ApplicationFormDataV2) => {
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      const response = await submitApplicationV2(data);
      setPaymentData(response);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to submit application");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset to initial state
  const handleReset = () => {
    setPaymentData(null);
    setSubmitError(null);
  };

  // Show payment QR after successful submission
  if (paymentData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-accent-50 to-neutral-100">
        <Header />
        <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <PaymentQR paymentData={paymentData} onReset={handleReset} />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-accent-50 to-neutral-100">
      <Header />

      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="mb-10 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
            Course Application
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-neutral-600">
            Fill in your details to apply for our AI Courses. Pay securely via UPI.
          </p>
        </div>

        {/* Loading State */}
        {loadingCourses && (
          <div className="flex flex-col items-center justify-center py-16">
            <div
              className="h-10 w-10 animate-spin rounded-full border-4 border-t-transparent"
              style={{ borderColor: `${brand.colors.primary}40`, borderTopColor: brand.colors.primary }}
            />
            <p className="mt-4 text-neutral-600">Loading courses...</p>
          </div>
        )}

        {/* Error State */}
        {courseError && (
          <div className="mx-auto max-w-md rounded-lg bg-red-50 p-6 text-center border border-red-200">
            <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mt-4 text-red-800">{courseError}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 text-sm font-medium text-red-600 hover:text-red-500"
            >
              Try again
            </button>
          </div>
        )}

        {/* Application Form */}
        {!loadingCourses && !courseError && (
          <>
            {submitError && (
              <div className="mb-6 rounded-lg bg-red-50 p-4 text-center border border-red-200">
                <p className="text-red-700">{submitError}</p>
              </div>
            )}

            <ApplicationFormV2
              courses={courses}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-16 border-t border-neutral-200 bg-white py-8">
        <div className="mx-auto max-w-7xl px-4 text-center">
          <img
            src={brand.logo.favicon}
            alt={brand.organizationName}
            className="mx-auto h-10 w-auto mb-4"
          />
          <p className="text-sm text-neutral-600">{brand.organizationName}</p>
          <p className="mt-1 text-sm text-neutral-500">{brand.slogan}</p>
          <div className="mt-4 flex items-center justify-center gap-4 text-sm text-neutral-600">
            <a href={`mailto:${brand.email}`} className="hover:text-brand-primary transition-colors">
              {brand.email}
            </a>
            <span>|</span>
            <a href={`tel:${brand.mobile.replace(/\s/g, "")}`} className="hover:text-brand-primary transition-colors">
              {brand.mobile}
            </a>
          </div>
          <p className="mt-4 text-xs text-neutral-400">
            © {new Date().getFullYear()} {brand.organizationName}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
